// WHAT: Hook for fetching and aggregating TimeTracQ data (shortfalls, tasks, overview)
// WHY:  Provides unified interface for loading all TimeTracQ data with abort control and timeout
// DOMAIN: Productivity → TimeTracQ
// DEPENDENCIES: core.invoke from Tauri API, getSafeAspectColor from shared utilities
// RISKS:
//   - NOTE: Multiple backend calls may cause performance issues
//   - NOTE: Abort controller may not work correctly with all backend commands
//   - NOTE: Timeout is hardcoded (30s) - may be too long or too short
//   - NOTE: Overview data aggregation is complex - may have edge cases

import type { SupabaseTask } from "./data/useSupabasetasks";

import { core } from "@tauri-apps/api";
import { useEffect, useState, useRef, useCallback } from "react";

import { getSafeAspectColor, type WorkspaceConfig } from "@/shared";

/**
 * WHAT:
 *   Extracts a number value from unknown input.
 *
 * WHY:
 *   Provides consistent number extraction logic for shortfall mapping.
 *
 * @param value - Value to check
 *
 * @returns Number value or 0
 *
 */
const getNumberValue = (value: unknown): number => {
  return typeof value === "number" ? value : 0;
};

/**
 * Top task interface
 */
export interface TopTask {
  /** Aspect name */
  aspect: string;
  /** Days until deadline */
  deadline_days: number;
  /** Task identifier */
  id: string;
  /** Task score (priority) */
  score: number;
  /** Task status */
  status: null | string;
  /** Task name */
  task: string;
}

/**
 * Shortfall entry interface
 */
export interface ShortfallEntry {
  /** Aspect name */
  child: string;
  /** Workspace name */
  parent: string;
  /** Planned hours monthly */
  planned: number;
  /** Planned hours until today */
  plannedUntilToday: number;
  /** Shortfall for full month */
  shortfallFullMonth: number;
  /** Shortfall until today */
  shortfallUntilToday: number;
  /** Entry status */
  status: string;
  /** Total hours worked */
  worked: number;
}

/**
 * WHAT:
 *   Hook that fetches and aggregates all TimeTracQ data including shortfalls,
 *   top tasks, overview statistics, and on-track factor.
 *
 * WHY:
 *   Centralizes data fetching logic and provides consistent interface for
 *   accessing TimeTracQ metrics.
 *
 * BEHAVIOR:
 *   - Fetches shortfalls from backend
 *   - Fetches top tasks by aspect
 *   - Calculates on-track factor
 *   - Aggregates workspace and aspect overview
 *   - Handles abort control and timeout
 *   - Manages loading state
 *
 * RISKS:
 *   - NOTE: Multiple backend calls may cause performance issues
 *   - NOTE: Abort controller may not work correctly with all backend commands
 *   - NOTE: Timeout is hardcoded (30s) - may be too long or too short
 *   - NOTE: Overview data aggregation is complex - may have edge cases
 *
 * @param root0
 *
 * @param root0.tasks
 *
 * @param root0.config
 *
 * @param root0.shortfallViewMode
 *
 * @returns {{ shortfalls: ShortfallEntry[], topTasks: TopTask[], workedToday: number, workedTotal: number, expectedTotal: number, onTrackFactor: number | undefined, workspaceOverview: unknown[], aspectOverview: unknown[], isLoading: boolean }} Aggregated TimeTracQ data
 *
 */
export function useDataFetcher({
  config,
  shortfallViewMode,
  tasks,
}: {
  tasks: SupabaseTask[];
  config: Record<string, Record<string, { color: string; hours: number }>>;
  shortfallViewMode: "full" | "today";
}) {
  const [topTasks, setTopTasks] = useState<TopTask[]>([]);
  const [shortfalls, setShortfalls] = useState<ShortfallEntry[]>([]);
  const [workedToday, setWorkedToday] = useState<number>(0);
  const [onTrackFactor, setOnTrackFactor] = useState<number | undefined>();
  const [workedTotal, setWorkedTotal] = useState(0);
  const [expectedTotal, setExpectedTotal] = useState(0);
  const [workspaceOverview, setWorkspaceOverview] = useState<
    { color: string; name: string; planned: number; total: number; worked: number }[]
  >([]);
  const [aspectOverview, setAspectOverview] = useState<
    { color: string; name: string; planned: number; total: number; worked: number }[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);
  const isLoadingReference = useRef(false);
  const abortControllerReference = useRef<AbortController | undefined>(undefined);

  // NOTE: Debug effects removed - were empty and not needed

  // NOTE: Wrapped fetchData with useCallback to prevent infinite loops
  const fetchData = useCallback(async () => {
    // NOTE: Cancel any ongoing request
    if (abortControllerReference.current) {
      abortControllerReference.current.abort();
    }

    // NOTE: Create new abort controller for this request
    abortControllerReference.current = new AbortController();

    if (isLoadingReference.current) {
      return;
    }

    isLoadingReference.current = true;
    setIsLoading(true);

    // NOTE: Add a timeout to prevent infinite loading
    const timeoutId = setTimeout(() => {
      isLoadingReference.current = false;
      setIsLoading(false);
    }, 30_000); // 30 second timeout

    // NOTE: We need config for overview data, even if no tasks
    if (Object.keys(config).length === 0) {
      isLoadingReference.current = false;
      setIsLoading(false);
      clearTimeout(timeoutId);
      return;
    }

    try {
      // NOTE: Calculate annual on-track factor
      const factorData = await core.invoke<{
        factor: number;
        worked: number;
        expected: number;
      }>("calculate_annual_on_track_factor");

      setOnTrackFactor(factorData.factor);
      setWorkedTotal(factorData.worked);
      setExpectedTotal(factorData.expected);

      // NOTE: Get top tasks by aspect
      const tops = await core.invoke<TopTask[]>("get_top_tasks_by_aspect", {
        tasks,
      });
      setTopTasks(tops);

      // NOTE: Calculate shortfalls
      const raw = await core.invoke<{
        shortfalls: {
          parent: string;
          child: string;
          [key: string]: unknown;
        }[];
        worked_today: number;
      }>("calculate_shortfalls", { appHandle: {} });

      setWorkedToday(raw.worked_today || 0);

      // NOTE: Map backend shortfall data to frontend format
      const mappedShortfalls: ShortfallEntry[] = raw.shortfalls.map((item) => {
        return {
          child: item.child,
          parent: item.parent,
          planned: getNumberValue(item.planned_hours_monthly),
          plannedUntilToday: getNumberValue(item.planned_until_today),
          shortfallFullMonth: getNumberValue(item.shortfall_full_month),
          shortfallUntilToday: getNumberValue(item.shortfall_until_today),
          status: typeof item.status === "string" ? item.status : "include",
          worked: getNumberValue(item.total_hours_worked),
        };
      });

      // NOTE: Sort shortfalls by selected view mode (descending)
      mappedShortfalls.sort((a, b) => {
        const aValue = shortfallViewMode === "today" ? a.shortfallUntilToday : a.shortfallFullMonth;
        const bValue = shortfallViewMode === "today" ? b.shortfallUntilToday : b.shortfallFullMonth;
        return bValue - aValue;
      });

      setShortfalls(mappedShortfalls);

      // NOTE: Aggregate workspace and aspect overview data
      const wsMap = new Map<string, { worked: number; planned: number }>();
      const aspData: {
        name: string;
        worked: number;
        planned: number;
        color: string;
      }[] = [];

      for (const entry of mappedShortfalls) {
        const aspColor =
          getSafeAspectColor(config as WorkspaceConfig, entry.parent, entry.child) ?? "#64748b";

        if (!wsMap.has(entry.parent)) {
          wsMap.set(entry.parent, { planned: 0, worked: 0 });
        }

        const ws = wsMap.get(entry.parent);
        if (ws) {
          ws.worked += entry.worked;
          ws.planned += entry.planned;
        }

        aspData.push({
          color: aspColor,
          name: `${entry.parent} / ${entry.child}`,
          planned: entry.planned,
          worked: entry.worked,
        });
      }

      // NOTE: Convert workspace map to array
      const workspaceData = [...wsMap.entries()].map(([name, data]) => ({
        color: getWorkspaceColor(config, name),
        name,
        planned: data.planned,
        total: data.worked,
        worked: data.worked,
      }));

      const aspectDataWithTotal = aspData.map((item) => ({
        ...item,
        total: item.worked,
      }));

      setWorkspaceOverview(workspaceData);
      setAspectOverview(aspectDataWithTotal);

      isLoadingReference.current = false;
      setIsLoading(false);
      clearTimeout(timeoutId);
    } catch {
      // NOTE: Error fetching data - reset loading state
      isLoadingReference.current = false;
      setIsLoading(false);
      clearTimeout(timeoutId);
    }
  }, [tasks, config, shortfallViewMode]); // NOTE: Dependencies moved here

  // NOTE: Only fetchData as dependency to prevent infinite loops
  useEffect(() => {
    void fetchData();

    // NOTE: Cleanup function to cancel ongoing requests
    return () => {
      if (abortControllerReference.current) {
        abortControllerReference.current.abort();
      }
    };
  }, [fetchData]); // NOTE: Only fetchData as dependency

  // NOTE: Separate useEffect for final state check without closure issues
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      // NOTE: Final state check - can be used for additional side effects if needed
      // NOTE: timeoutId is intentionally unused here
    }, 100);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [workspaceOverview, aspectOverview]);

  return {
    aspectOverview,
    expectedTotal,
    isLoading,
    onTrackFactor,
    shortfalls,
    topTasks,
    workedToday,
    workedTotal,
    workspaceOverview,
  };
}

/**
 * WHAT:
 *   Returns color for workspace from config.
 *
 * WHY:
 *   Provides consistent color mapping for workspace visualization.
 *
 * BEHAVIOR:
 *   - Gets first aspect color as workspace color
 *   - Returns default gray if workspace not found
 *
 * @param config
 *
 * @param workspace
 *
 * @returns Color hex code for the workspace
 *
 */
function getWorkspaceColor(
  config: Record<string, Record<string, { color: string; hours: number }>>,
  workspace: string,
): string {
  const wsEntry = config[workspace];
  if (!wsEntry) {
    return "#64748b";
  }
  const firstAspect = Object.keys(wsEntry).find((k) => k !== "color");
  return (firstAspect && wsEntry[firstAspect]?.color) ?? "#64748b";
}
